import React, { useEffect, useRef } from 'react';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useAppState } from "@/context/AppStateContext";
import { useAppChat } from "@/context/AppChatContext";
import { useWebsites } from "@/context/WebsitesContext";
import Chat from "@/pages/Chat";
import ContentEditor from "@/pages/ContentEditor";
import Dashboard from "@/pages/Dashboard";
import WebsiteSitemap from "@/pages/WebsiteSitemap";
import Media from "@/pages/Media";
import PlannedContent from "@/pages/PlannedContent";
import RightPaneIframe from "@/components/RightPaneIframe";
import Settings from "@/pages/Settings";
import WebsiteManager from "@/pages/WebsiteManager";

interface SharedContentLayoutProps {
  children?: React.ReactNode;
}

const SharedContentLayout: React.FC<SharedContentLayoutProps> = ({ children }) => {
  const { currentRightPaneUrl } = useAppState();
  const { isEditingPost } = useAppChat();
  const { currentWebsite } = useWebsites();

  // Log only when these values actually change to avoid per-render spam
  const prevStateRef = useRef<{ url: string | null; isEditing: boolean; hasWebsite: boolean } | null>(null);
  useEffect(() => {
    const nextState = {
      url: currentRightPaneUrl,
      isEditing: !!isEditingPost,
      hasWebsite: !!currentWebsite,
    };
    if (
      !prevStateRef.current ||
      prevStateRef.current.url !== nextState.url ||
      prevStateRef.current.isEditing !== nextState.isEditing ||
      prevStateRef.current.hasWebsite !== nextState.hasWebsite
    ) {
      console.log("🔄 SharedContentLayout: State changed", nextState);
      prevStateRef.current = nextState;
    }
  }, [currentRightPaneUrl, isEditingPost, currentWebsite]);

  // Determine what to show in the right pane
  const renderRightPane = () => {
    if (
      currentRightPaneUrl?.startsWith("/content-editor/") ||
      currentRightPaneUrl?.startsWith("/post/")
    ) {
      return <ContentEditor />;
    } else if (
      currentRightPaneUrl === "/dashboard" ||
      currentRightPaneUrl?.startsWith("/dashboard")
    ) {
      console.log("📊 SharedContentLayout: Rendering Dashboard directly");
      return <Dashboard />;
    } else if (
      currentRightPaneUrl === "/sitemap" ||
      currentRightPaneUrl?.startsWith("/sitemap")
    ) {
      console.log("🧭 SharedContentLayout: Rendering WebsiteSitemap directly");
      return <WebsiteSitemap />;
    } else if (
      currentRightPaneUrl === "/websites" ||
      currentRightPaneUrl?.startsWith("/websites")
    ) {
      console.log("🌐 SharedContentLayout: Rendering Websites directly");
      return <WebsiteManager />;
    } else if (
      currentRightPaneUrl === "/media" ||
      currentRightPaneUrl?.startsWith("/media")
    ) {
      console.log("🖼️ SharedContentLayout: Rendering Media directly");
      return <Media />;
    } else if (
      currentRightPaneUrl === "/settings" ||
      currentRightPaneUrl?.startsWith("/settings")
    ) {
      console.log("⚙️ SharedContentLayout: Rendering Settings directly");
      return <Settings />;
    } else if (
      currentRightPaneUrl === "/planned-content" ||
      currentRightPaneUrl?.startsWith("/planned-content")
    ) {
      console.log("🗓️ SharedContentLayout: Rendering PlannedContent directly");
      return <PlannedContent />;
    } else {
      // Log only when the URL changes (handled above). Keep a minimal log here for first mount.
      // console.log("🖼️ SharedContentLayout: Rendering RightPaneIframe with url:", currentRightPaneUrl);
      return <RightPaneIframe url={currentRightPaneUrl} />;
    }
  };

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-x-2">
      <ResizablePanel>
        <Chat />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel className='!overflow-y-scroll'>
        {renderRightPane()}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};

export default SharedContentLayout;
